/**
 * Application shell: the top bar and role-scoped navigation.
 *
 * The navigation is derived from the permission matrix rather than hard-coded per role.
 * That means adding a screen requires declaring which permission it needs, and a role
 * that lacks the permission simply never sees the link — so the nav cannot drift out of
 * sync with what the Firestore rules will actually allow.
 */
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  Activity,
  Building2,
  ClipboardList,
  FlaskConical,
  Users,
  FileSearch,
  Heart,
  LogOut,
  Search,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'
import { ROLE_LABELS, type Permission } from '@rescure/shared'
import { useAuth } from '../../lib/auth'
import { Button } from '../ui/primitives'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Permission required to see this link. Undefined means "any signed-in user". */
  permission?: Permission
}

/** Navigation per top-level area. */
const NAV: Record<string, NavItem[]> = {
  '/patient': [
    { to: '/patient', label: 'My matches', icon: Sparkles },
    { to: '/patient/search', label: 'Find studies', icon: Search },
    { to: '/patient/record', label: 'My health record', icon: Heart },
    { to: '/patient/privacy', label: 'Privacy & consent', icon: ShieldCheck },
  ],
  '/clinician': [
    { to: '/clinician', label: 'My panel', icon: Users, permission: 'patient:read:panel' },
    { to: '/clinician/trials', label: 'Browse trials', icon: FlaskConical, permission: 'trial:read' },
    { to: '/clinician/referrals', label: 'My referrals', icon: ClipboardList, permission: 'candidate:shortlist' },
  ],
  '/org': [
    { to: '/org', label: 'Overview', icon: Activity },
    { to: '/org/pipeline', label: 'Candidate pipeline', icon: ClipboardList, permission: 'trial:read' },
    { to: '/org/feasibility', label: 'Feasibility', icon: FileSearch, permission: 'cohort:query' },
    { to: '/org/trials', label: 'Trials', icon: FlaskConical, permission: 'trial:read' },
    { to: '/org/settings', label: 'Organization', icon: Building2, permission: 'org:manage' },
  ],
}

function areaFor(pathname: string): keyof typeof NAV | null {
  if (pathname.startsWith('/patient')) return '/patient'
  if (pathname.startsWith('/clinician')) return '/clinician'
  if (pathname.startsWith('/org')) return '/org'
  return null
}

export function AppShell() {
  const { profile, signOut, can, home } = useAuth()
  const navigate = useNavigate()
  const area = areaFor(window.location.pathname) ?? (home as keyof typeof NAV)
  const items = (NAV[area] ?? []).filter((item) => !item.permission || can(item.permission))

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-20 border-b border-[var(--border-subtle)] bg-[var(--surface-card)]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-2.5">
          <button
            type="button"
            onClick={() => navigate(home)}
            className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]"
          >
            <span className="grid size-6 place-items-center rounded bg-brand-600 text-xs font-bold text-white">R</span>
            Rescure
          </button>

          <nav aria-label="Main" className="hidden flex-1 items-center gap-0.5 md:flex">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                // `end` on the area root so "/patient" is not highlighted while on
                // "/patient/search".
                end={item.to === area}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                    isActive
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]'
                  )
                }
              >
                <item.icon aria-hidden className="size-3.5" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {profile ? (
              <div className="hidden text-right sm:block">
                <p className="text-xs font-medium text-[var(--text-primary)]">{profile.displayName || profile.email}</p>
                <p className="text-2xs text-[var(--text-muted)]">{ROLE_LABELS[profile.role]}</p>
              </div>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              icon={LogOut}
              onClick={async () => {
                await signOut()
                // Explicit `state: null` so this navigation can never inherit a
                // `from` some other in-flight redirect had already attached to the
                // `/signin` history entry (e.g. a 401 firing around the same time).
                navigate('/signin', { replace: true, state: null })
              }}
            >
              Sign out
            </Button>
          </div>
        </div>

        {/* Mobile navigation: a horizontally scrolling strip rather than a hamburger,
            because these are 3-5 destinations and a menu would hide all of them. */}
        <nav aria-label="Main" className="flex gap-1 overflow-x-auto border-t border-[var(--border-subtle)] px-3 py-1.5 md:hidden">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === area}
              className={({ isActive }) =>
                clsx(
                  'flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium',
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-[var(--text-secondary)]'
                )
              }
            >
              <item.icon aria-hidden className="size-3.5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-5">
        <Outlet />
      </main>

      <footer className="border-t border-[var(--border-subtle)] px-4 py-3 text-center text-2xs text-[var(--text-muted)]">
        Rescure · MIT Sandbox Sp26 · Trial data from ClinicalTrials.gov · All patients are synthetic
      </footer>
    </div>
  )
}
